import * as Dialog from '@radix-ui/react-dialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@/components/Icons/Close';
import { DownloadFilled } from '@/components/Icons/DownloadFilled';
import { PdfPreview } from '@/components/PdfPreview';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useAttachmentBlobUrl } from '@/hooks/useAttachmentBlobUrl';
import { useCloseOnBack } from '@/hooks/useCloseOnBack';
import type { MailAttachment } from '@/types/mail';
import { hasPdfExtension, triggerDownload } from '@/utils/file';

interface Props {
  attachment: MailAttachment;
  onClose: () => void;
}

export const MailAttachmentPreview: React.FC<Props> = ({
  attachment,
  onClose,
}) => {
  const { t } = useTranslation();
  const blobUrl = useAttachmentBlobUrl(attachment.id, attachment.name);

  const isPdf = hasPdfExtension(attachment.name);

  useCloseOnBack(true, onClose);

  const handleDownload = () => {
    if (!blobUrl) {
      return;
    }

    triggerDownload(blobUrl, attachment.name);
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              onClose();
            }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#131313] outline-none md:bg-transparent md:p-4"
        >
          <Dialog.Title className="sr-only">{attachment.name}</Dialog.Title>

          <div className="flex size-full max-h-full max-w-full flex-col md:size-auto md:w-full md:max-w-[1052px] md:gap-4 md:rounded-2xl md:bg-card md:p-5 md:pb-9 md:shadow-modal">
            <div className="flex shrink-0 items-center justify-between px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:hidden">
              <button
                type="button"
                onClick={handleDownload}
                aria-label={t('home.newsDetail.downloadLabel')}
                className="flex size-6 cursor-pointer items-center justify-center text-white"
              >
                <DownloadFilled className="size-6" />
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('global.close')}
                className="flex size-6 cursor-pointer items-center justify-center text-white"
              >
                <Close className="size-6" />
              </button>
            </div>

            <header className="hidden items-center gap-3 md:flex">
              <p className="min-w-0 flex-1 truncate text-headline-20 text-foreground">
                {attachment.name}
              </p>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-2xl bg-primary px-3 py-1 text-subhead-14 text-white transition-colors hover:bg-primary-hover"
              >
                <DownloadFilled className="size-4" />
                {t('home.newsDetail.downloadLabel')}
              </button>
              <button
                type="button"
                aria-label={t('global.close')}
                onClick={onClose}
                className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-muted-foreground"
              >
                <Close className="size-5" />
              </button>
            </header>

            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden md:h-[75vh] md:flex-none md:rounded-xl md:bg-card">
              {!blobUrl && <PageSpinner />}
              {blobUrl && isPdf && (
                <PdfPreview url={blobUrl} title={attachment.name} />
              )}
              {blobUrl && !isPdf && (
                <img
                  src={blobUrl}
                  alt={attachment.name}
                  draggable={false}
                  className="size-full object-contain select-none"
                />
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
